import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Beaker, Leaf, ShieldCheck, Sparkles } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { BlogByline } from "@/components/BlogByline";
import { ReadWithLlm } from "@/components/ReadWithLlm";
import { buildBlogHead } from "@/lib/blogMeta";
import { RelatedReading } from "@/components/RelatedReading";
import { References } from "@/components/References";
import { ClusterLinks } from "@/components/ClusterLinks";
import { AuthorityFlow } from "@/components/AuthorityFlow";
import { relatedArticles } from "@/lib/clusters";
import heroAsset from "@/assets/blog/sleep-anxiety.jpg.asset.json";
import { ClinicalProductPitch } from "@/components/ClinicalProductPitch";

const URL = "https://truwe.in/blog/natural-remedies-sleep-anxiety-india";
const TITLE = "Natural Ways to Deal With Sleep Anxiety (India, 2026) | TruWe";

export const Route = createFileRoute("/blog/natural-remedies-sleep-anxiety-india")({
  component: Article,
  head: () =>
    buildBlogHead({
      slug: "natural-remedies-sleep-anxiety-india",
      title: TITLE,
      description:
        "Evidence-based natural remedies for sleep anxiety in India, magnesium, glycine, sleep hygiene, breathwork and when to see a clinician. What actually helps racing thoughts at night.",
      ogTitle: "Natural Remedies for Sleep Anxiety (India)",
      ogDescription:
        "Racing thoughts at 2 AM? A practical, evidence-based India guide to natural ways to calm the nervous system before bed.",
      headline: "Natural Ways to Deal With Sleep Anxiety",
      breadcrumbName: "Natural Remedies for Sleep Anxiety (India)",
      datePublished: "2026-07-07",
      dateModified: "2026-07-25",
      ogImage: `https://truwe.in${heroAsset.url}`,
      faqs: [
        {
          q: "What's the fastest natural fix for anxious sleeplessness?",
          a: "A dark, cool room, no screens 60 minutes before bed, and 4-7-8 breathing. Add magnesium bisglycinate 200-300 mg one hour before bed, most people notice a shift inside a week.",
        },
        {
          q: "Is melatonin safe long term for Indians?",
          a: "Short-term (up to 3 months) melatonin has good safety data, but it's a hormone, not a nutrient. In India it needs a prescription. Address the root nervous-system arousal before layering hormones.",
        },
        {
          q: "Does magnesium actually help with anxiety at night?",
          a: "Yes. Magnesium is a cofactor for GABA (the brain's calming neurotransmitter) and blunts the cortisol response. Trials on 300-500 mg/day report meaningful reductions in mild-to-moderate anxiety scores.",
        },
        {
          q: "When should sleep anxiety be treated by a doctor?",
          a: "If insomnia lasts >3 weeks despite lifestyle changes, includes panic attacks, or comes with weight loss, low mood or intrusive thoughts, see a physician or psychiatrist.",
        },
      ],
    }),
});

function Article() {
  return (
    <article className="bg-background pb-24 pt-28 md:pt-36">
      <div className="mx-auto max-w-3xl px-4 md:px-8">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
            Clinical Library · Sleep & Calm
          </p>
          <h1 className="mt-3 font-display text-4xl font-extrabold leading-tight tracking-tight text-brand-deep md:text-5xl">
            Natural ways to deal with sleep anxiety
          </h1>
          <BlogByline date="2026-07-07" readingTime="10 min read" />
          <div className="mt-4">
            <ReadWithLlm title={TITLE} url={URL} kind="article" />
          </div>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            Sleep anxiety is what happens when the moment you switch the light off, the
            brain switches on. Tomorrow's meetings, half-remembered arguments, that email
            you didn't send. This is a nervous-system problem, not a willpower problem -
            and there is a short list of things that actually shift it.
          </p>
        <figure className="mt-8 overflow-hidden rounded-3xl border border-border">
            <img
              src={heroAsset.url}
              alt="Natural remedies for sleep anxiety, a calm bedtime routine with herbal tea"
              width={1200}
              height={630}
              loading="eager"
              className="h-auto w-full"
            />
          </figure>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            What sleep anxiety actually is
          </h2>
          <p className="mt-3 text-base text-foreground/85">
            Falling asleep needs the parasympathetic (rest-and-digest) branch of the
            nervous system to take over. In sleep anxiety, cortisol and adrenaline are
            still elevated at 11 PM, the sympathetic branch is stuck in "on". The fix
            isn't more willpower; it's giving the body the signals and nutrients it
            needs to switch modes.
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            The evidence-based natural stack
          </h2>
          <div className="mt-5 space-y-4">
            {[
              {
                name: "Magnesium bisglycinate (200-300 mg, 1 hr before bed)",
                use: "GABA cofactor + lowers cortisol",
                note: "The single most-studied mineral for sleep and anxiety. Bisglycinate is calming without the laxative effect of citrate or oxide. Glycine itself is pro-sleep.",
              },
              {
                name: "L-theanine (100-200 mg after dinner)",
                use: "Alpha-wave calm without sedation",
                note: "The amino acid in green tea. Raises alpha brain waves that 'settled but awake' feeling which primes the transition to sleep.",
              },
              {
                name: "Ashwagandha (300-600 mg KSM-66)",
                use: "Adaptogen, lowers evening cortisol",
                note: "Multiple Indian RCTs show reduced perceived stress and improved sleep quality at 8 weeks. Best taken at dinner, not bedtime.",
              },
              {
                name: "Glycine (3 g at bedtime)",
                use: "Body temperature drop = deeper sleep",
                note: "Slightly cools core body temperature, which mimics what naturally happens as you fall asleep. Studied in sleep-onset insomnia in Japan.",
              },
              {
                name: "Chamomile (a strong cup, 30 min before bed)",
                use: "Apigenin, mild GABAergic",
                note: "Not a knockout, but a gentle nudge that pairs well with breathwork.",
              },
            ].map((row) => (
              <div key={row.name} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
                <div className="flex items-center gap-2">
                  <Beaker className="h-4 w-4 text-brand" />
                  <h3 className="font-display text-lg font-semibold text-brand-deep">{row.name}</h3>
                </div>
                <p className="mt-1 text-sm font-medium text-brand">{row.use}</p>
                <p className="mt-2 text-sm text-foreground/80">{row.note}</p>
              </div>
            ))}
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            The 90-minute wind-down that actually works
          </h2>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>
              <strong>T-90 min:</strong> Last coffee/tea was &gt;8 hours ago. Dim overhead
              lights; switch to warm bedside lamps.
            </li>
            <li>
              <strong>T-60 min:</strong> Screens off (or blue-light filter + brightness
              at minimum). Magnesium bisglycinate + a small square of dark chocolate is
              the ritual.
            </li>
            <li>
              <strong>T-30 min:</strong> Warm shower, the post-shower core-temperature
              drop is a strong sleep signal. Write down tomorrow's top 3 tasks so the
              brain stops looping them.
            </li>
            <li>
              <strong>T-10 min:</strong> In bed, 4-7-8 breathing for 4 rounds. Inhale 4,
              hold 7, exhale 8. Activates the vagus nerve, drops heart rate.
            </li>
          </ul>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="mt-12 font-display text-2xl font-bold text-brand-deep md:text-3xl">
            What to stop doing
          </h2>
          <ul className="mt-4 space-y-3 text-base text-foreground/85">
            <li>
              <strong>Doomscrolling in bed.</strong> Each swipe is a fresh dopamine ping
, the opposite of a downshifting nervous system.
            </li>
            <li>
              <strong>"Just one" evening whisky.</strong> Alcohol fragments deep sleep
              even at one drink; you'll wake at 3 AM more anxious.
            </li>
            <li>
              <strong>Late workouts (after 8 PM).</strong> Cortisol stays elevated for
              hours. Morning or afternoon training is far better for sleep-anxious
              people.
            </li>
            <li>
              <strong>Checking the clock at 2 AM.</strong> Turn the bedside clock away.
              Every check is another cortisol spike.
            </li>
          </ul>
        </Reveal>

        <Reveal delay={0.05}>
          <div className="mt-14 rounded-3xl border border-brand/15 bg-gradient-to-br from-brand/5 via-background to-brand-deep/5 p-8 md:p-10">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand">
              <Sparkles className="h-4 w-4" /> The TruWe formulation
            </div>
            <h2 className="mt-3 font-display text-2xl font-bold text-brand-deep md:text-3xl">
              Mag Absorb Pro, the sleep-anxiety mineral, done right
            </h2>
            <p className="mt-3 text-base text-foreground/85">
              Four absorbable magnesium forms, <strong>bisglycinate for GABA support,
              Aquamin® marine minerals, chloride and aspartate</strong> at doses
              targeted at nervous-system calm and muscle relaxation. Take one 60 minutes
              before bed with warm water.
            </p>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {[
                { icon: Leaf, label: "Non-habit forming" },
                { icon: ShieldCheck, label: "Third-party tested" },
                { icon: Beaker, label: "Research-backed doses" },
              ].map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2 rounded-xl bg-background/70 px-3 py-3 text-sm text-foreground/85 ring-1 ring-border"
                >
                  <Icon className="h-4 w-4 text-brand" />
                  {label}
                </div>
              ))}
            </div>
            <Link
              to="/products/$handle"
              params={{ handle: "mag-absorb-pro" }}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white shadow-luxury transition hover:bg-brand-deep"
            >
              Explore Mag Absorb Pro <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>

        <Reveal delay={0.05}>
          <ClinicalProductPitch cluster="magnesium" />
          <ClusterLinks cluster="magnesium" />
          <AuthorityFlow cluster="magnesium" />
          <References slug="natural-remedies-sleep-anxiety-india" />
          <RelatedReading articles={relatedArticles("natural-remedies-sleep-anxiety-india")} />
        </Reveal>

        <Reveal delay={0.05}>
          <p className="mt-12 text-xs text-muted-foreground">
            Educational only. Persistent insomnia, panic attacks or intrusive thoughts
            need a clinician, natural remedies complement care, they don't replace it.
          </p>
        </Reveal>
      </div>
    </article>
  );
}
